import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Lock, Search, Truck } from "lucide-react";
import { apiBaseUrl, authHeaders, isSuperAdmin } from "./shared";

// Same naive-UTC parsing fix DriverCalendar.jsx / DispatcherBoard.jsx already
// use for every *_datetime field -- plain `new Date(x)` would silently shift
// by the browser's own UTC offset.
function parseUtcNaive(isoLike) {
  if (!isoLike) return null;
  const iso = isoLike.endsWith("Z") || /[+-]\d\d:\d\d$/.test(isoLike) ? isoLike : `${isoLike}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

function zonedParts(date, tzName) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tzName, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = {};
  fmt.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}` };
}

// The grid's day columns are business calendar days, not "whatever timezone
// the dispatcher's own laptop happens to be in" -- driver_time_off/
// truck_time_off only ever persist an absolute UTC instant (the local zone
// picked in the modal is used once, at save time, to convert). So cell
// membership has to be judged against a single fixed operating timezone
// (matching the modal's own default), or the same record would render on a
// different -- or an extra -- day depending on the viewer's OS timezone.
const BUSINESS_TZ = "America/Chicago";
function zonedDateStartUtc(year, month, date, tzName) {
  const guess = new Date(Date.UTC(year, month, date, 0, 0, 0));
  const wallAtGuess = zonedParts(guess, tzName);
  const wallAsUtc = new Date(`${wallAtGuess.date}T${wallAtGuess.time}:00Z`);
  return new Date(guess.getTime() + (guess.getTime() - wallAsUtc.getTime()));
}

function startOfWeek(d) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function formatShort(d) {
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}
function toDateInput(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

// "Home" used to be its own selectable reason alongside PTO, but the two
// mean the same thing operationally (driver unavailable, matching should
// skip them) -- driver_time_off.py's active_or_upcoming_time_off already
// only ever excludes reason='pto' rows from matching, never 'home'/'other',
// so a dispatcher picking "Home" was silently NOT blocking that driver from
// being matched. Rather than also teach the matching exclusion about a
// second equivalent reason, "Home" is retired as a choice going forward --
// everything that means "this driver is out" is just PTO now. REASON_COLOR/
// REASON_LABEL keep a "home" entry so any pre-existing driver_time_off rows
// saved before this change still render correctly instead of showing a
// blank/undefined status.
// "location" (migration 0051) is a dispatcher's manual override of what
// this board shows for a truck on a given day -- the live system's own
// projection (a real load's destination) can be wrong or simply not what
// actually happened, and this is how a dispatcher corrects it right on the
// board. Deliberately still truck_time_off, same table as Shop, but never
// affects matching (see truck_time_off.py's own reason filter) -- only
// Shop is a real out-of-service signal.
// Labeled "Home Time" not "PTO" -- these drivers aren't paid time off,
// they're just home. The stored reason key stays "pto" everywhere (matching
// exclusion, existing rows, backend queries all key off it) -- this is a
// display-only rename.
const REASONS = [
  { key: "location", label: "Location", color: "#38bdf8" },
  { key: "shop", label: "Shop", color: "#f2a33d" },
  { key: "pto", label: "Home Time", color: "#a78bfa" },
  { key: "other", label: "Other", color: "#4cae6d" },
];
// Truck-level reasons (don't need a driver assigned) vs driver-level ones
// (do) -- StatusModal disables the driver-level buttons when there's no
// driver on this truck yet.
const TRUCK_LEVEL_REASONS = new Set(["location", "shop"]);
const REASON_COLOR = { home: "#a78bfa", location: "#38bdf8", shop: "#f2a33d", pto: "#a78bfa", other: "#4cae6d" };
const REASON_LABEL = { home: "Home Time", location: "Location", shop: "Shop", pto: "Home Time", other: "Other" };

export default function LoadBoard() {
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loads, setLoads] = useState([]);
  const [driverTimeOff, setDriverTimeOff] = useState([]);
  const [truckTimeOff, setTruckTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [dispatcherFilter, setDispatcherFilter] = useState("");
  const [search, setSearch] = useState("");
  const [statusModal, setStatusModal] = useState(null); // { truck, day, existing } | null
  const [lockedInfo, setLockedInfo] = useState(null); // the real load being explained
  const [claimConfirm, setClaimConfirm] = useState(null); // the truck pending a "Mine" confirmation
  const [saving, setSaving] = useState(false);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  const currentUserRoles = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("roles") || "[]"); } catch { return []; }
  }, []);
  const canConfirmReturn =
    currentUserRoles.includes("admin") ||
    currentUserRoles.includes("super_admin") ||
    currentUserRoles.includes("dispatcher");
  // Reassigning a truck to ANY dispatcher (not just yourself) is a
  // super-admin-only capability -- a regular dispatcher can still claim an
  // unassigned/someone-else's truck for THEMSELVES via "Assign to me"
  // below, but picking an arbitrary third dispatcher for a truck they
  // don't own is an org-structure change, not a day-to-day dispatch action.
  const canReassignAnyone = isSuperAdmin();

  function reload() {
    setLoading(true);
    // Everything that fills a board CELL -- booked loads, driver PTO/other,
    // truck shop/other -- is fetched scoped to the VISIBLE WEEK: only rows
    // whose date range overlaps these 7 days, and re-fetched when the week
    // changes. (Fetching whole tables capped at 500 broke once the system
    // passed ~500 loads: the cap returned mostly old rows and dropped the
    // current week's, leaving the board empty.) Week boundaries are computed
    // in BUSINESS_TZ so they line up exactly with the per-day cell math below.
    const wkStartUtc = zonedDateStartUtc(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), BUSINESS_TZ);
    const wkEndDay = addDays(weekStart, 7);
    const wkEndUtc = zonedDateStartUtc(wkEndDay.getFullYear(), wkEndDay.getMonth(), wkEndDay.getDate(), BUSINESS_TZ);
    const startStr = wkStartUtc.toISOString().slice(0, 19); // naive-UTC, matches stored *_datetime
    const endStr = wkEndUtc.toISOString().slice(0, 19);
    // A row overlaps the week iff its start is before the week ends AND its end
    // is after the week starts.
    const overlap = (startField, endField) => encodeURIComponent(JSON.stringify({ conditions: [
      { field: startField, operator: "<=", value: endStr },
      { field: endField, operator: ">=", value: startStr },
    ]}));
    Promise.all([
      axios.get(`${apiBaseUrl}/trucks/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/drivers/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/search/loads/?filter_json=${overlap("pickup_datetime", "delivery_datetime")}&limit=500`, authHeaders()),
      axios.get(`${apiBaseUrl}/search/driver_time_off/?filter_json=${overlap("start_datetime", "end_datetime")}&limit=500`, authHeaders()),
      axios.get(`${apiBaseUrl}/search/truck_time_off/?filter_json=${overlap("start_datetime", "end_datetime")}&limit=500`, authHeaders()),
    ])
      .then(([tRes, dRes, lRes, dtoRes, ttoRes]) => {
        setTrucks(tRes.data.records || tRes.data || []);
        setDrivers(dRes.data.records || dRes.data || []);
        setLoads(lRes.data.records || lRes.data || []);
        setDriverTimeOff(dtoRes.data.records || dtoRes.data || []);
        setTruckTimeOff(ttoRes.data.records || ttoRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  // Re-fetch on mount and whenever the viewed week changes -- weekStart is the
  // only input the scoped fetch depends on.
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const driverById = useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.id] = d; });
    return map;
  }, [drivers]);

  // Same fallback the backend already uses for HOS compliance/scorecard
  // truck resolution: trucks.current_driver_id is only stamped by Motive
  // sync or the explicit "Assign driver" button, NOT by assigning a load to
  // a truck -- so a truck a dispatcher just booked a load onto can still
  // show no live current_driver_id yet. drivers.last_truck_id is the
  // sticky, driver-side record of "which truck was I really on," and using
  // it here means this board doesn't wrongly tell a dispatcher "no driver
  // assigned" (and disable PTO for that driver) right after they picked one.
  const driverByLastTruckId = useMemo(() => {
    const map = {};
    drivers.forEach((d) => {
      const lastTruckId = d.last_truck_id?.id || d.last_truck_id;
      if (lastTruckId) map[lastTruckId] = d;
    });
    return map;
  }, [drivers]);

  const driverForTruck = useCallback((t) => {
    const driverId = t.current_driver_id?.id || t.current_driver_id;
    return (driverId && driverById[driverId]) || driverByLastTruckId[t.id] || null;
  }, [driverById, driverByLastTruckId]);

  // A multi-day PTO stretch entered as several adjacent single-day rows
  // (one per "+" click, rather than one row spanning the whole stretch) is
  // real and common -- a dispatcher shouldn't have to enter it any
  // differently. Overdue has to key off the driver's LATEST-ending 'pto'
  // row, not each row in isolation: with 3 days off entered as 3 separate
  // day rows, the first day's own row ending is not the driver failing to
  // return -- 2 more days remain. Only once the driver's last scheduled
  // day has passed, with no admin confirmation since, are they overdue.
  const latestPtoRowIdByDriverId = useMemo(() => {
    const map = {};
    driverTimeOff.forEach((r) => {
      if (r.reason !== "pto") return;
      const did = r.driver_id?.id || r.driver_id;
      const end = parseUtcNaive(r.end_datetime);
      if (!did || !end) return;
      const current = map[did];
      if (!current || end > current.end) map[did] = { id: r.id, end };
    });
    return map;
  }, [driverTimeOff]);

  // Deliberately NOT inferred from a new load being assigned -- see
  // confirm-driver-return's own docstring: a dispatcher covering for a
  // driver they know personally could otherwise clear this just by
  // booking the next load.
  function isOverduePto(row) {
    if (!row || row.reason !== "pto" || row.return_confirmed_at) return false;
    const did = row.driver_id?.id || row.driver_id;
    if (row.id !== latestPtoRowIdByDriverId[did]?.id) return false;
    const end = parseUtcNaive(row.end_datetime);
    return !!end && end < new Date();
  }

  // Keyed by driver id (not just cells falling in the currently-displayed
  // week) so an overdue PTO from weeks ago still shows on the row header
  // no matter which week a dispatcher happens to have open.
  const overdueTimeOffByDriverId = useMemo(() => {
    const map = {};
    driverTimeOff.forEach((r) => {
      if (!isOverduePto(r)) return;
      const did = r.driver_id?.id || r.driver_id;
      if (!did) return;
      if (!map[did]) map[did] = [];
      map[did].push(r);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverTimeOff, latestPtoRowIdByDriverId]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const isCurrentWeek = isSameDay(weekStart, startOfWeek(new Date()));

  const dispatcherOptions = useMemo(() => {
    const seen = new Map();
    trucks.forEach((t) => {
      const id = t.dispatcher_id?.id || t.dispatcher_id;
      const name = t.dispatcher_id?.display_value;
      if (id && name && !seen.has(id)) seen.set(id, name);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [trucks]);

  // Same default this app already uses on the Operational Center: a
  // dispatcher opens the board seeing just their own trucks, not the whole
  // fleet's -- based on whether the CURRENT USER actually dispatches
  // anything (present in dispatcherOptions), not on role, so an admin who
  // also dispatches some trucks gets the same "mine first" default a
  // regular dispatcher does. An admin (or anyone) who dispatches nothing
  // has no "mine" to default to, so this leaves the filter on "All" --
  // dispatchers can always broaden back to All themselves via this same
  // dropdown, this only decides what's selected on first load. Only fires
  // once real dispatcher data is in, and only once ever (a later manual
  // filter change, including back to "All", must never get silently reset
  // by a later trucks refetch).
  const currentUserId = localStorage.getItem("id");
  const dispatcherFilterInitRef = useRef(false);
  useEffect(() => {
    if (dispatcherFilterInitRef.current || dispatcherOptions.length === 0) return;
    dispatcherFilterInitRef.current = true;
    if (dispatcherOptions.some((d) => d.id === currentUserId)) setDispatcherFilter(currentUserId);
  }, [dispatcherOptions, currentUserId]);

  const visibleTrucks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trucks.filter((t) => {
      if (dispatcherFilter) {
        const did = t.dispatcher_id?.id || t.dispatcher_id;
        if (did !== dispatcherFilter) return false;
      }
      if (q) {
        const driverName = driverForTruck(t)?.driver_name || "";
        const hay = `${t.unit_number || ""} ${driverName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (showOverdueOnly) {
        const driverId = driverForTruck(t)?.id;
        if (!driverId || !overdueTimeOffByDriverId[driverId]?.length) return false;
      }
      return true;
    });
  }, [trucks, dispatcherFilter, search, driverForTruck, showOverdueOnly, overdueTimeOffByDriverId]);

  // Per truck x day resolution -- a dispatcher's own truck_time_off row
  // (Shop OR a manual Location override) wins over EVERYTHING else,
  // including a real load that also covers this day -- explicit ask: the
  // system's own projection can be wrong, and the board needs to show what
  // the dispatcher actually knows, not what a load's dates imply. The real
  // load is never touched by this (still bookable, still the source of
  // truth for actual dispatch) -- it's carried along as `load` on the
  // returned cell so the UI can still surface it (a small info affordance)
  // even when an override is what's actually displayed. Falls through to
  // driver-level status (pto/home/other), then empty, same as before.
  function resolveCell(truck, day) {
    const dayStart = zonedDateStartUtc(day.getFullYear(), day.getMonth(), day.getDate(), BUSINESS_TZ);
    const nextDay = addDays(day, 1);
    const dayEnd = zonedDateStartUtc(nextDay.getFullYear(), nextDay.getMonth(), nextDay.getDate(), BUSINESS_TZ);

    // A truck can genuinely have two loads overlapping the same calendar day
    // (one delivering that morning, a new one picked up that same afternoon)
    // -- .find() alone would return whichever happened to come first in the
    // fetched array (arbitrary, not chronological), so a same-day second
    // load wasn't reliably shown over the one it replaced. Picking the
    // candidate with the LATEST pickup reflects what the truck is actually
    // doing most recently as of this day.
    const dayCandidates = loads.filter((l) => {
      if ((l.status || "").toLowerCase() === "cancelled") return false;
      const assignedId = l.assigned_truck_id?.id || l.assigned_truck_id;
      if (assignedId !== truck.id) return false;
      const pickup = parseUtcNaive(l.pickup_datetime);
      const delivery = parseUtcNaive(l.delivery_datetime);
      if (!pickup || !delivery) return false;
      return rangesOverlap(dayStart, dayEnd, pickup, delivery);
    });
    const load = dayCandidates.length
      ? dayCandidates.reduce((latest, l) =>
          parseUtcNaive(l.pickup_datetime) > parseUtcNaive(latest.pickup_datetime) ? l : latest
        )
      : null;

    const override = truckTimeOff.find((r) => {
      const tid = r.truck_id?.id || r.truck_id;
      if (tid !== truck.id) return false;
      const s = parseUtcNaive(r.start_datetime);
      const e = parseUtcNaive(r.end_datetime);
      return s && e && rangesOverlap(dayStart, dayEnd, s, e);
    });
    if (override) return { type: override.reason || "shop", row: override, load };

    // Same current_driver_id -> last_truck_id fallback driverForTruck already
    // uses for the row header's own driver name -- without it, a truck
    // whose live current_driver_id hasn't been (re-)stamped yet by Motive
    // sync or the "Assign driver" button silently showed the driver's name
    // (via the fallback) while any PTO entered for that same driver just
    // never appeared here at all, with no visible reason why. Confirmed
    // real: a driver's own freshly-saved PTO rows didn't show up the
    // moment their truck's current_driver_id was unset.
    const driverId = truck.current_driver_id?.id || truck.current_driver_id || driverByLastTruckId[truck.id]?.id;
    const dto = driverId
      ? driverTimeOff.find((r) => {
          const did = r.driver_id?.id || r.driver_id;
          if (did !== driverId) return false;
          const s = parseUtcNaive(r.start_datetime);
          const e = parseUtcNaive(r.end_datetime);
          return s && e && rangesOverlap(dayStart, dayEnd, s, e);
        })
      : null;

    if (load) {
      // A multi-day load showing the SAME destination on every single day
      // it spans was arguably the more misleading default -- it reads as
      // "he's in Boston" starting today, not "he's en route to Boston,
      // arriving in 4 days." Only the real delivery day gets the
      // destination; every day before it just says the truck is out on a
      // load, without asserting a location the truck may not have reached
      // yet.
      const delivery = parseUtcNaive(load.delivery_datetime);
      const isDeliveryDay = !!delivery && rangesOverlap(dayStart, dayEnd, delivery, delivery);
      // A load on the truck doesn't make the driver's own scheduled home
      // time any less real -- it just wins the cell's display (confirmed
      // real: this silently hid several genuinely-unconfirmed, past-due
      // PTO rows behind "On a load" cells, including on the actual
      // delivery day, with zero visual trace anywhere in the current
      // week's view). Only reason 'pto' counts here, same restriction
      // isOverduePto uses -- 'shop'/'other'/'location' rows have no
      // "confirm return" concept.
      //
      // Only flagged on the ONE day containing the PTO row's own start
      // instant, not every day it merely overlaps -- a short PTO window
      // stored in UTC can straddle local midnight once converted to
      // BUSINESS_TZ (confirmed real: a driver's 03:00-13:59 UTC window
      // lands at 22:00-08:59 America/Chicago, genuinely touching BOTH
      // adjacent calendar cells), which would otherwise show the exact
      // same row's flag/button twice for one PTO stretch.
      const dtoStart = dto ? parseUtcNaive(dto.start_datetime) : null;
      const ptoConflict =
        dto && dto.reason === "pto" && !dto.return_confirmed_at && dtoStart && dtoStart >= dayStart && dtoStart < dayEnd
          ? dto
          : null;
      return { type: "load", load, isDeliveryDay, ptoConflict };
    }

    // Anchor the overdue flag + "Confirm return" to the ONE cell holding the window END
    // instant (last home-time day), not every overlapping day (truck #459: a Sep21-24 row
    // showed it on all 3 cells once ended). Row-header overdue badge still flags regardless.
    if (dto) {
      const dtoEnd = parseUtcNaive(dto.end_datetime);
      const overdueOnEndDay = isOverduePto(dto) && dtoEnd && dtoEnd >= dayStart && dtoEnd < dayEnd;
      return { type: dto.reason || "pto", row: dto, isOverdue: overdueOnEndDay };
    }

    return { type: "empty" };
  }

  function confirmDriverReturn(id) {
    setSaving(true);
    axios
      .post(`${apiBaseUrl}/scripted/fleet/confirm-driver-return`, { id }, authHeaders())
      .then(() => reload())
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't confirm return"))
      .finally(() => setSaving(false));
  }

  function saveTruckStatus(truckId, id, reason, startLocal, endLocal, timezone, note, location) {
    setSaving(true);
    axios
      .post(`${apiBaseUrl}/scripted/fleet/save-truck-status`,
        { id: id || undefined, truck_id: truckId, reason, location: location || null, start_local: startLocal, end_local: endLocal, timezone, note: note || null },
        authHeaders())
      .then(() => { setStatusModal(null); reload(); })
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't save status"))
      .finally(() => setSaving(false));
  }

  function saveDriverStatus(driverId, id, reason, startLocal, endLocal, timezone, note) {
    setSaving(true);
    axios
      .post(`${apiBaseUrl}/scripted/fleet/save-time-off`,
        { id: id || undefined, driver_id: driverId, reason, start_local: startLocal, end_local: endLocal, timezone, note: note || null },
        authHeaders())
      .then(() => { setStatusModal(null); reload(); })
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't save status"))
      .finally(() => setSaving(false));
  }

  // Same real, writable column DispatcherBoard.jsx's own "Assign dispatcher"
  // control already uses (trucks.dispatcher_id) -- this just gives the
  // board itself a way to do the same reassignment inline, instead of
  // dispatchers having to leave the board and go find the truck in the
  // Operational Center just to change who's handling it.
  function assignDispatcher(truckId, dispatcherId) {
    setSaving(true);
    axios
      .put(`${apiBaseUrl}/trucks/${truckId}`, { dispatcher_id: dispatcherId || null }, authHeaders())
      .then(() => reload())
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't update the truck's dispatcher"))
      .finally(() => setSaving(false));
  }

  function clearStatus(existing) {
    const url = TRUCK_LEVEL_REASONS.has(existing.type)
      ? `${apiBaseUrl}/truck_time_off/${existing.row.id}`
      : `${apiBaseUrl}/driver_time_off/${existing.row.id}`;
    axios.delete(url, authHeaders())
      .then(() => { setStatusModal(null); reload(); })
      .catch((err) => window.alert(err.response?.data?.detail || "Couldn't clear status"));
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Load Board</h1>
          <div style={styles.subtitle}>One shared weekly board -- replaces per-dispatcher spreadsheets.</div>
        </div>
        <div style={styles.controls}>
          <div style={styles.searchWrap}>
            <Search size={13} style={styles.searchIcon} />
            <input
              type="search" placeholder="Search truck or driver…" value={search}
              onChange={(e) => setSearch(e.target.value)} style={styles.searchInput}
            />
          </div>
          <select value={dispatcherFilter} onChange={(e) => setDispatcherFilter(e.target.value)} style={styles.select}>
            <option value="">All dispatchers</option>
            {dispatcherOptions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <div style={styles.weekNav}>
            <button style={styles.navBtn} onClick={() => setWeekStart((d) => addDays(d, -7))} aria-label="Previous week"><ChevronLeft size={16} color="#1e2e30" strokeWidth={2.5} style={{ width: 16, height: 16, flexShrink: 0 }} /></button>
            <div style={styles.weekLabel}>
              <CalendarDays size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              {formatShort(days[0])} - {formatShort(days[6])}
            </div>
            <button style={styles.navBtn} onClick={() => setWeekStart((d) => addDays(d, 7))} aria-label="Next week"><ChevronRight size={16} color="#1e2e30" strokeWidth={2.5} style={{ width: 16, height: 16, flexShrink: 0 }} /></button>
          </div>
          <button style={{ ...styles.todayBtn, ...(isCurrentWeek ? styles.todayBtnActive : {}) }} onClick={() => setWeekStart(startOfWeek(new Date()))}>
            This week
          </button>
          <button
            style={{ ...styles.todayBtn, ...(showOverdueOnly ? styles.overdueFilterBtnActive : {}) }}
            onClick={() => setShowOverdueOnly((v) => !v)}
            title="Drivers whose home time end date has passed with no confirmed return"
          >
            Overdue returns only
          </button>
        </div>
      </div>

      <div style={styles.legend}>
        <LegendItem color="#5b9bd5" label="Booked load -- click info icon to view" />
        <LegendItem color="#38bdf8" label="Location override" />
        <LegendItem color="#f2a33d" label="Shop" />
        <LegendItem color="#a78bfa" label="Home Time" />
        <LegendItem color="#4cae6d" label="Other" />
        <LegendItem color="#ef4444" label="Home time overdue -- past end date, return not confirmed" />
        <span style={styles.legendHint}>Click any cell -- even a booked load -- to override it</span>
      </div>

      <div style={styles.boardShell}>
        {loading && <div style={styles.loadingMsg}>Loading…</div>}
        {!loading && visibleTrucks.length === 0 && <div style={styles.loadingMsg}>No trucks match.</div>}
        {!loading && visibleTrucks.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.corner}>Truck / Driver</th>
                {days.map((d, i) => (
                  <th key={i} style={{ ...styles.daycol, ...(isSameDay(d, new Date()) ? styles.daycolToday : {}) }}>
                    <div style={styles.dname}>{d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}</div>
                    <div style={styles.ddate}>{formatShort(d)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleTrucks.map((t) => {
                const driver = driverForTruck(t);
                return (
                  <tr key={t.id}>
                    <td style={styles.rowhead}>
                      <div style={styles.truckHeaderRow}>
                        {/* Opens the Operational Center in a new tab with
                            this exact truck pre-selected (same ?truck=
                            deep-link the Alerts page/Command Center strip
                            already use) -- explicit ask: jumping to a
                            truck's full detail shouldn't require leaving
                            the board to go search for it by hand. Plain
                            <a target="_blank">, not react-router's Link --
                            a new tab is always a fresh full page load
                            regardless, so there's nothing client-side
                            routing would buy here. */}
                        <a
                          href={`/dispatch/operational-center?truck=${t.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.truckno}
                          title="Open this truck in the Operational Center (new tab)"
                        >
                          #{t.unit_number}
                        </a>
                        {/* Equipment type -- Reefer vs Dry Van (vs Flatbed
                            etc.) matters for what a dispatcher can even
                            offer against a given load, so it needs to be
                            visible at a glance right on the board, not
                            buried in the truck's own detail panel. Reefer
                            gets its own color since that's the distinction
                            dispatchers actually care about day to day. */}
                        {t.equipment_type_id?.display_value && (
                          <span
                            style={{
                              ...styles.equipmentTag,
                              ...(t.equipment_type_id.display_value.toLowerCase().includes("reefer") ? styles.equipmentTagReefer : {}),
                            }}
                          >
                            {t.equipment_type_id.display_value}
                          </span>
                        )}
                      </div>
                      {driver ? (
                        <>
                          <div style={styles.drivername}>{driver.driver_name}</div>
                          {driver.phone && <div style={styles.phoneline}>{driver.phone}</div>}
                        </>
                      ) : (
                        <div style={styles.unassigned}>No driver assigned</div>
                      )}
                      {(() => {
                        const currentDispatcherId = t.dispatcher_id?.id || t.dispatcher_id || "";
                        return (
                          <div style={styles.dispatcherRow}>
                            {/* Full reassign-to-anyone power (an editable
                                dropdown, applied immediately) is super-admin
                                only -- everyone else gets the plain read-only
                                tag. Claiming a truck for YOURSELF is still
                                available to anyone, one click, gated behind a
                                confirmation so it's deliberate, not
                                accidental. */}
                            {canReassignAnyone ? (
                              <select
                                value={currentDispatcherId}
                                onChange={(e) => assignDispatcher(t.id, e.target.value)}
                                disabled={saving}
                                style={styles.dispatcherSelect}
                                title="Reassign this truck to any dispatcher (super admin)"
                              >
                                <option value="">— unassigned —</option>
                                {dispatcherOptions.map((d) => (
                                  <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                                {currentDispatcherId && !dispatcherOptions.some((d) => d.id === currentDispatcherId) && (
                                  <option value={currentDispatcherId}>{t.dispatcher_id?.display_value || "Current"}</option>
                                )}
                              </select>
                            ) : (
                              t.dispatcher_id?.display_value && (
                                <div style={styles.dispatchertag}>{t.dispatcher_id.display_value}</div>
                              )
                            )}
                            {currentDispatcherId !== currentUserId && (
                              <button
                                style={styles.claimBtn}
                                disabled={saving}
                                onClick={() => setClaimConfirm(t)}
                                title="Assign this truck to me"
                              >
                                Assign to me
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      {/* Visible regardless of which week is currently open
                          -- an overdue PTO from weeks ago should never
                          silently fall out of view just because today's
                          board is showing a different week. */}
                      {driver && overdueTimeOffByDriverId[driver.id]?.length > 0 && (
                        <div
                          style={styles.overdueBadge}
                          title={`Home time ended ${parseUtcNaive(overdueTimeOffByDriverId[driver.id][0].end_datetime)?.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) || ""} -- return not confirmed`}
                        >
                          <AlertTriangle size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
                          Home time overdue
                        </div>
                      )}
                    </td>
                    {days.map((day, i) => {
                      const cell = resolveCell(t, day);
                      return (
                        <td key={i} style={styles.cell}>
                          {cell.type === "load" ? (
                            // No override yet -- still clickable to set one
                            // (explicit ask: even a day a real load already
                            // covers must be overridable), the load record
                            // itself is only ever a click on the info button.
                            <div
                              style={{ ...styles.blockLoad, cursor: "pointer" }}
                              onClick={() => setStatusModal({ truck: t, day, driver, existing: null, load: cell.load })}
                              title="Click to override what this day shows -- or use the info icon to view the real load"
                            >
                              <button
                                style={styles.loadInfoBtn}
                                onClick={(e) => { e.stopPropagation(); setLockedInfo(cell.load); }}
                                title="View the real load -- edit at the load record"
                              >
                                <Lock size={9} />
                              </button>
                              {/* The load winning this cell doesn't mean the
                                  driver's own scheduled home time stopped
                                  being real -- including on the actual
                                  delivery day (a truck reaching Chicago on
                                  the same day its driver was supposed to be
                                  home is still an unconfirmed return, not a
                                  resolved one). See resolveCell's own
                                  comment on ptoConflict. */}
                              {cell.ptoConflict && (
                                <span
                                  style={styles.conflictFlag}
                                  title={`Home time was scheduled through ${parseUtcNaive(cell.ptoConflict.end_datetime)?.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) || ""} -- this truck had a load instead, return not confirmed`}
                                >
                                  <AlertTriangle size={9} />
                                </span>
                              )}
                              {/* Destination only on the real delivery day
                                  (per dispatcher request) -- every day
                                  before it says "On a load" instead of
                                  repeating the destination as if the truck
                                  were already there (see resolveCell's own
                                  comment on isDeliveryDay). Full origin ->
                                  destination is still shown when the info
                                  icon is clicked (see lockedInfo modal
                                  below). */}
                              {cell.isDeliveryDay ? (
                                <>
                                  <div style={styles.route}>{cell.load.destination_location}</div>
                                  <div style={styles.meta}>
                                    Delivers {parseUtcNaive(cell.load.delivery_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) || "—"}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div style={styles.route}>On a load</div>
                                  <div style={styles.meta}>
                                    Delivers {parseUtcNaive(cell.load.delivery_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) || "—"}
                                  </div>
                                </>
                              )}
                              {/* Gate on end<now (isOverduePto), same as the pure-PTO cell -- a load on the PTO start day was surfacing this on day 1 while home time remained (truck #25576). */}
                              {cell.ptoConflict && isOverduePto(cell.ptoConflict) && canConfirmReturn && (
                                <button
                                  style={styles.confirmReturnBtn}
                                  onClick={(e) => { e.stopPropagation(); confirmDriverReturn(cell.ptoConflict.id); }}
                                  title="Confirm this driver has actually returned from home time"
                                >
                                  Confirm return
                                </button>
                              )}
                            </div>
                          ) : cell.type === "empty" ? (
                            <div
                              style={styles.blockEmpty}
                              onClick={() => setStatusModal({ truck: t, day, driver, existing: null })}
                            >+</div>
                          ) : (() => {
                            // Overdue overrides the normal PTO color entirely --
                            // this is the one status that can't just be clicked
                            // away (clearStatus is still available, but resolving
                            // the RED specifically requires an explicit Confirm
                            // Return action, see confirm-driver-return).
                            const blockColor = cell.isOverdue ? "#ef4444" : REASON_COLOR[cell.type];
                            return (
                              <div
                                style={{ ...styles.blockStatus, background: `color-mix(in srgb, ${blockColor} 18%, var(--card))`, borderColor: `color-mix(in srgb, ${blockColor} 45%, transparent)` }}
                                onClick={() => setStatusModal({ truck: t, day, driver, existing: cell, load: cell.load })}
                              >
                                {/* A real load can still underlie an override
                                    day (see resolveCell's own comment) -- this
                                    is the only way back to it once the
                                    override is what's actually showing. */}
                                {cell.load && (
                                  <button
                                    style={styles.loadInfoBtn}
                                    onClick={(e) => { e.stopPropagation(); setLockedInfo(cell.load); }}
                                    title="A real load also covers this day -- view it"
                                  >
                                    <Lock size={9} />
                                  </button>
                                )}
                                <div style={{ ...styles.reasonLabel, color: blockColor === "var(--muted)" ? "var(--ink-2)" : blockColor }}>
                                  {cell.type === "location" ? (cell.row?.location || "Location") : (REASON_LABEL[cell.type] || cell.type)}
                                  {cell.isOverdue && " -- overdue"}
                                </div>
                                {cell.row?.note && <div style={styles.noteText}>{cell.row.note}</div>}
                                {cell.isOverdue && canConfirmReturn && (
                                  <button
                                    style={styles.confirmReturnBtn}
                                    onClick={(e) => { e.stopPropagation(); confirmDriverReturn(cell.row.id); }}
                                    title="Confirm this driver has actually returned"
                                  >
                                    Confirm return
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {statusModal && (
        <StatusModal
          truck={statusModal.truck}
          driver={statusModal.driver}
          day={statusModal.day}
          existing={statusModal.existing}
          load={statusModal.load}
          saving={saving}
          onCancel={() => setStatusModal(null)}
          onClear={() => clearStatus(statusModal.existing)}
          onSave={(reason, startLocal, endLocal, tz, note, location) => {
            if (TRUCK_LEVEL_REASONS.has(reason)) {
              const existingId = statusModal.existing && TRUCK_LEVEL_REASONS.has(statusModal.existing.type) ? statusModal.existing.row.id : null;
              saveTruckStatus(statusModal.truck.id, existingId, reason, startLocal, endLocal, tz, note, location);
            } else {
              const existingId = statusModal.existing && !TRUCK_LEVEL_REASONS.has(statusModal.existing.type) ? statusModal.existing.row.id : null;
              saveDriverStatus(statusModal.driver.id, existingId, reason, startLocal, endLocal, tz, note);
            }
          }}
        />
      )}

      {lockedInfo && (
        <div style={styles.modalOverlay} onClick={() => setLockedInfo(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>{lockedInfo.origin_location} → {lockedInfo.destination_location}</div>
            <div style={styles.lockedNote}>
              This day comes from a real booked load. To change pickup, delivery, or cancel it, edit the load itself
              (Loads, or the truck's Current Load tab in the Operational Center) -- not from the board.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button style={styles.cancelBtn} onClick={() => setLockedInfo(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {claimConfirm && (
        <div style={styles.modalOverlay} onClick={() => setClaimConfirm(null)}>
          <div style={styles.claimCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.claimIconWrap}><Truck size={20} /></div>
            <div style={styles.claimTitle}>Assign truck #{claimConfirm.unit_number} to you?</div>
            <div style={styles.claimBody}>
              {claimConfirm.dispatcher_id?.display_value
                ? <>Currently handled by <b>{claimConfirm.dispatcher_id.display_value}</b> -- this makes it yours instead.</>
                : "This truck isn't handled by anyone yet."}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button style={styles.cancelBtn} onClick={() => setClaimConfirm(null)}>Cancel</button>
              <button
                style={styles.saveBtn}
                disabled={saving}
                onClick={() => { assignDispatcher(claimConfirm.id, currentUserId); setClaimConfirm(null); }}
              >
                {saving ? "Assigning…" : "Yes, it's mine"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <span style={styles.legendItem}>
      <span style={{ ...styles.swatch, background: color }} />
      {label}
    </span>
  );
}

function StatusModal({ truck, driver, day, existing, load, saving, onCancel, onClear, onSave }) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState(
    TIMEZONE_OPTIONS.some((o) => o.value === browserTz) ? browserTz : "America/Chicago"
  );
  // A pre-existing "home" row (saved before Home/PTO were merged) still
  // needs a real, selectable REASONS key if the dispatcher reopens it to
  // edit -- "pto" is the closest since that's what it now means.
  const [reason, setReason] = useState((existing?.type === "home" ? "pto" : existing?.type) || (driver ? "pto" : "shop"));
  const [startDate, setStartDate] = useState(existing ? zonedParts(parseUtcNaive(existing.row.start_datetime), timezone).date : toDateInput(day));
  const [startTime, setStartTime] = useState(existing ? zonedParts(parseUtcNaive(existing.row.start_datetime), timezone).time : "00:00");
  const [endDate, setEndDate] = useState(existing ? zonedParts(parseUtcNaive(existing.row.end_datetime), timezone).date : toDateInput(day));
  const [endTime, setEndTime] = useState(existing ? zonedParts(parseUtcNaive(existing.row.end_datetime), timezone).time : "23:59");
  const [note, setNote] = useState(existing?.row?.note || "");
  const [location, setLocation] = useState(existing?.row?.location || "");

  function submit() {
    onSave(reason, `${startDate}T${startTime}:00`, `${endDate}T${endTime}:00`, timezone, note, location);
  }

  const driverReasonsDisabled = !driver;
  const locationRequired = reason === "location";
  const canSubmit = !locationRequired || location.trim().length > 0;

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>{existing ? "Edit status" : "Set status"} -- Truck #{truck.unit_number}</div>

        {load && (
          // A real load already covers this day -- surfaced here, not
          // hidden, so overriding it is a deliberate choice, not an
          // accidental one made without realizing a real booking exists.
          <div style={styles.lockedNote}>
            A real load already covers this day: delivers to {load.destination_location},{" "}
            {parseUtcNaive(load.delivery_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) || "no date"}.
            {" "}Setting a status here overrides what the board SHOWS for this day -- the load itself is untouched.
          </div>
        )}

        <div style={styles.reasonRow}>
          {REASONS.map((r) => {
            const disabled = !TRUCK_LEVEL_REASONS.has(r.key) && driverReasonsDisabled;
            return (
              <button
                key={r.key}
                disabled={disabled}
                onClick={() => setReason(r.key)}
                style={{
                  ...styles.reasonBtn,
                  ...(reason === r.key ? { borderColor: r.color, color: r.color } : {}),
                  ...(disabled ? { opacity: 0.4, cursor: "default" } : {}),
                }}
                title={disabled ? "No driver assigned to this truck" : undefined}
              >
                <span style={{ ...styles.reasonDot, background: r.color }} />
                {r.label}
              </button>
            );
          })}
        </div>

        {reason === "location" && (
          <div style={styles.modalRow}>
            <label style={styles.modalLabel}>City</label>
            <input
              type="text" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Chicago, IL" style={styles.modalNoteInput}
            />
          </div>
        )}

        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Zone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={styles.modalDateInput}>
            {TIMEZONE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={styles.modalDateInput} />
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={styles.modalTimeInput} />
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Until</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={styles.modalDateInput} />
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={styles.modalTimeInput} />
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Note</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" style={styles.modalNoteInput} />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          {existing && <button style={styles.clearBtn} onClick={onClear}>Clear</button>}
          <button style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.saveBtn} onClick={submit} disabled={saving || !canSubmit}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  controls: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  searchWrap: { position: "relative" },
  searchIcon: { position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    fontSize: 12.5, padding: "7px 10px 7px 26px", borderRadius: 8, border: "1px solid var(--line)",
    background: "var(--card)", color: "var(--ink)", width: 190,
  },
  select: { fontSize: 12.5, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)" },
  weekNav: { display: "flex", alignItems: "center", gap: 4, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: 4 },
  navBtn: { width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--card-2, #f1f5f4)", border: "1px solid var(--line)", borderRadius: 6, cursor: "pointer", color: "#1e2e30" },
  weekLabel: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", minWidth: 140, textAlign: "center" },
  todayBtn: { fontSize: 11.5, fontWeight: 700, color: "var(--brand)", background: "rgba(29, 111, 216,0.08)", border: "1px solid rgba(29, 111, 216,0.25)", borderRadius: 999, padding: "7px 14px", cursor: "pointer" },
  todayBtnActive: { background: "var(--brand)", color: "#fff" },
  overdueFilterBtnActive: { background: "#ef4444", color: "#fff", borderColor: "#ef4444" },

  legend: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", fontSize: 11.5, color: "var(--muted)", marginBottom: 12, padding: "9px 14px", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },
  legendHint: { marginLeft: "auto" },
  swatch: { width: 10, height: 10, borderRadius: 3, display: "inline-block" },

  boardShell: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-xs)", overflow: "auto", maxHeight: "74vh" },
  loadingMsg: { padding: 24, fontSize: 13, color: "var(--muted)" },
  table: { borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: 1080 },
  corner: { position: "sticky", top: 0, left: 0, zIndex: 3, background: "var(--card-2, var(--card))", minWidth: 210, textAlign: "left", padding: "10px 14px", fontSize: 10.5, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", borderBottom: "1px solid var(--line)", borderRight: "1px solid var(--line)" },
  daycol: { position: "sticky", top: 0, zIndex: 2, background: "var(--card-2, var(--card))", padding: "9px 8px", textAlign: "center", minWidth: 140, borderBottom: "1px solid var(--line)", borderRight: "1px solid var(--line)" },
  daycolToday: { background: "rgba(29, 111, 216,0.12)" },
  dname: { fontSize: 11, fontWeight: 800, color: "var(--ink-2)" },
  ddate: { fontSize: 10.5, color: "var(--muted)", marginTop: 1 },

  rowhead: { position: "sticky", left: 0, zIndex: 1, background: "var(--card)", padding: "10px 14px", verticalAlign: "top", minWidth: 210, borderBottom: "1px solid var(--line)", borderRight: "1px solid var(--line)" },
  truckHeaderRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  truckno: { fontSize: 13, fontWeight: 800, color: "var(--ink)", textDecoration: "none", cursor: "pointer" },
  equipmentTag: {
    fontSize: 9.5, fontWeight: 700, color: "var(--muted)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 5, padding: "1px 6px", display: "inline-block",
  },
  equipmentTagReefer: { color: "#38bdf8", background: "rgba(56,189,248,0.1)", borderColor: "rgba(56,189,248,0.35)" },
  drivername: { fontSize: 12, color: "var(--ink-2)", marginTop: 2 },
  phoneline: { fontSize: 10.5, color: "var(--muted)", marginTop: 1 },
  unassigned: { fontSize: 12, color: "var(--muted)", fontStyle: "italic", marginTop: 2 },
  dispatchertag: { fontSize: 9.5, fontWeight: 700, color: "var(--muted)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 5, padding: "1px 6px", display: "inline-block", marginTop: 4 },
  dispatcherRow: { display: "flex", alignItems: "center", gap: 4, marginTop: 4 },
  dispatcherSelect: {
    fontSize: 9.5, fontWeight: 700, color: "var(--muted)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 5, padding: "1px 4px", maxWidth: 110,
  },
  claimBtn: {
    fontSize: 9, fontWeight: 700, color: "var(--brand)", background: "rgba(29, 111, 216,0.08)",
    border: "1px solid rgba(29, 111, 216,0.25)", borderRadius: 5, padding: "1px 6px", cursor: "pointer", flexShrink: 0,
  },
  overdueBadge: { fontSize: 9.5, fontWeight: 700, color: "#ef4444", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 5, padding: "2px 6px", display: "inline-block", marginTop: 4 },

  cell: { padding: 5, verticalAlign: "top", borderBottom: "1px solid var(--line)", borderRight: "1px solid var(--line)" },
  blockLoad: { borderRadius: 7, padding: "7px 9px", fontSize: 11, lineHeight: 1.4, minHeight: 46, background: "rgba(91,155,213,0.14)", border: "1px solid rgba(91,155,213,0.4)", position: "relative" },
  // Own click target (not just a static icon) -- the cell body's click
  // now opens the override editor, so viewing the real underlying load
  // needs its own explicit affordance, on both a plain load cell and an
  // overridden one (see resolveCell's own comment on why a load can still
  // exist under either).
  loadInfoBtn: {
    position: "absolute", top: 4, right: 4, width: 16, height: 16, display: "flex",
    alignItems: "center", justifyContent: "center", padding: 0, border: "none", borderRadius: 4,
    background: "rgba(2,6,23,0.12)", color: "var(--ink)", opacity: 0.75, cursor: "pointer",
  },
  // Opposite corner from loadInfoBtn -- a load winning the cell shouldn't
  // make an unconfirmed, overlapping PTO invisible (see resolveCell's own
  // ptoConflict comment). Purely a hover-title indicator, not a click
  // target -- the actual action is the confirmReturnBtn below it.
  conflictFlag: {
    position: "absolute", top: 4, left: 4, width: 16, height: 16, display: "flex",
    alignItems: "center", justifyContent: "center", borderRadius: 4,
    background: "rgba(239,68,68,0.18)", color: "#ef4444",
  },
  route: { fontWeight: 700, color: "var(--ink)" },
  confirmReturnBtn: {
    marginTop: 4, fontSize: 9.5, fontWeight: 700, color: "#fff", background: "#ef4444",
    border: "none", borderRadius: 5, padding: "3px 7px", cursor: "pointer", width: "100%",
  },
  meta: { color: "var(--muted)", fontSize: 10, marginTop: 2 },
  blockEmpty: { minHeight: 46, border: "1.5px dashed var(--line)", borderRadius: 7, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 16 },
  blockStatus: { minHeight: 46, borderRadius: 7, padding: "7px 9px", fontSize: 11, border: "1px solid", cursor: "pointer" },
  reasonLabel: { fontWeight: 700 },
  noteText: { color: "var(--muted)", fontSize: 10, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

  modalOverlay: { position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  modalCard: { width: 400, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,.4)" },
  modalTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 14 },
  lockedNote: { fontSize: 12, color: "var(--muted)", lineHeight: 1.55 },
  claimCard: {
    width: 340, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14,
    padding: "22px 20px 18px", boxShadow: "0 20px 60px rgba(0,0,0,.4)", textAlign: "center",
  },
  claimIconWrap: {
    width: 44, height: 44, borderRadius: "50%", background: "rgba(29, 111, 216,0.12)", color: "var(--brand)",
    display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px",
  },
  claimTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)", marginBottom: 6 },
  claimBody: { fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 },
  reasonRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 },
  reasonBtn: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", cursor: "pointer" },
  reasonDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  modalRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  modalLabel: { fontSize: 12, color: "var(--muted)", width: 42, flexShrink: 0 },
  modalTimeInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  modalDateInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  modalNoteInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  cancelBtn: { fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer" },
  clearBtn: { fontSize: 12, fontWeight: 600, color: "#e05d5d", background: "transparent", border: "1px solid rgba(224,93,93,0.35)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", marginRight: "auto" },
  saveBtn: { fontSize: 12, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer" },
};
